// React import not needed for JSX in modern React/TypeScript
import { router } from '@inertiajs/react';
import OptimizedImage from './OptimizedImage';
import ExplicitContentBadge from './ExplicitContentBadge';
import styles from './Card.module.css';

export interface CardItem {
  id: number;
  name: string;
  category?: string;
  external_url?: string;
  button_text?: string;
  image?: string | null; // Already WebP from backend
  slug?: string;
  description?: string;
  url?: string;
  status?: string;
  explicit?: boolean;
}

interface CardProps {
  item: CardItem;
  showCategory?: boolean;
  showButton?: boolean;
  imageHeight?: string;
  trackingEvent?: string;
  priority?: boolean; // For LCP optimization
}

const formatStatusLabel = (status: string) =>
  status.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

const Card: React.FC<CardProps> = ({
  item,
  showCategory = true,
  showButton = true,
  imageHeight = 'h-64',
  trackingEvent = 'product-click',
  priority = false,
}) => {
  const handlePrimaryClick = () => {
    if (item.url) {
      router.visit(item.url);
    }
  };

  return (
    <div
      className={`bg-base-100 border border-base-200 rounded-xl shadow-lg flex flex-col items-center p-6 group h-full cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl ${styles.Card}`}
      tabIndex={0}
      role="button"
      data-testid="card"
      onClick={e => {
        if (
          (e.target as HTMLElement)
            .closest('a')
            ?.classList.contains(styles['card-secondary-action'])
        )
          return;
        handlePrimaryClick();
      }}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          handlePrimaryClick();
        }
      }}
    >
      <div className="relative w-full mb-6">
        <div
          className={`w-full ${imageHeight} flex items-center justify-center bg-base-100 rounded-lg overflow-hidden`}
        >
          <OptimizedImage
            src={item.image}
            alt={item.name}
            className="object-contain w-full h-full"
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : 'auto'}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
        </div>
      </div>
      <div className="text-center mb-4 flex-1">
        <h3 className="text-xl font-semibold mb-2 text-base-content">
          <a
            href={item.url || '#'}
            className={styles['card-primary-action']}
            tabIndex={-1}
            onClick={e => e.preventDefault()}
            data-testid="card-title"
          >
            {item.name}
          </a>
        </h3>
        {item.status && item.status !== 'active' && (
          <div
            className={`mb-3 inline-flex rotate-[-6deg] items-center border-2 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] shadow-sm ${
              item.status === 'planned'
                ? 'border-blue-500 bg-blue-50/90 text-blue-700'
                : 'border-red-500 bg-red-50/90 text-red-600'
            }`}
          >
            {formatStatusLabel(item.status)}
          </div>
        )}
        {item.explicit && <ExplicitContentBadge className="mb-3" />}
        {showCategory && item.category && (
          <div className="text-sm text-base-content/75 uppercase tracking-wide mb-4">
            {item.category}
          </div>
        )}
        {/* Descriptions are not shown in cards */}
      </div>
      {showButton && item.external_url && (
        <a
          href={item.external_url}
          target="_blank"
          rel="noopener noreferrer"
          {...(item.slug && {
            'data-umami-event': trackingEvent,
            'data-umami-event-value': item.slug,
            'data-umami-event-context': 'card',
          })}
          className={`bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors inline-block mt-2 ${styles['card-secondary-action']}`}
        >
          {item.button_text || 'View Details'}
        </a>
      )}
    </div>
  );
};

export default Card;
